export const VALUATION_STORAGE_KEY = "aiproperty:valuation-submission";

// Shown next to the tick box and stored with the request as the consent record.
export const VALUATION_CONSENT_TEXT =
  "I agree to be contacted about my valuation. My details are kept private.";

export const PROPERTY_TYPES = ["hdb", "ec", "private-condo"] as const;

export type PropertyType = (typeof PROPERTY_TYPES)[number];

export type ValuationPayload = {
  propertyType: PropertyType;
  postalCode: string;
  unitNumber: string;
  unitSize?: string;
  renovationYear?: string;
  renovationAmount?: string;
  // HDB only: "yes" or "no". Years stayed is asked only when the MOP is not met.
  hdbMopMet?: "yes" | "no";
  hdbYearsStayed?: string;
  name: string;
  email: string;
  mobile: string;
  consent: boolean;
};

export type ValuationErrors = Partial<Record<keyof ValuationPayload | "form", string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Singapore mobiles start with 8 or 9; the backend rejects anything else.
const MOBILE_PATTERN = /^(?:\+?65)?[89]\d{7}$/;
const UNIT_PATTERN = /^#?[A-Za-z0-9]{1,4}(?:-[A-Za-z0-9]{1,4})?$/;

export function validateValuationPayload(input: unknown): {
  data: ValuationPayload | null;
  errors: ValuationErrors;
} {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { data: null, errors: { form: "Please check the form and try again." } };
  }

  const body = input as Record<string, unknown>;
  const propertyType = typeof body.propertyType === "string" ? body.propertyType : "";
  const postalCode = typeof body.postalCode === "string" ? body.postalCode.trim() : "";
  const unitNumber = typeof body.unitNumber === "string" ? body.unitNumber.trim() : "";
  const unitSize = typeof body.unitSize === "string" ? body.unitSize.trim() : "";
  const renovationYear =
    typeof body.renovationYear === "string" ? body.renovationYear.trim() : "";
  const renovationAmount =
    typeof body.renovationAmount === "string" ? body.renovationAmount.trim() : "";
  const hdbMopMet = typeof body.hdbMopMet === "string" ? body.hdbMopMet : "";
  const hdbYearsStayed =
    typeof body.hdbYearsStayed === "string" ? body.hdbYearsStayed.trim() : "";
  const name = typeof body.name === "string" ? body.name.trim() : "";
  const email = typeof body.email === "string" ? body.email.trim() : "";
  const mobile = typeof body.mobile === "string" ? body.mobile.replace(/[ -]/g, "") : "";
  const consent = body.consent === true;
  const errors: ValuationErrors = {};

  if (!PROPERTY_TYPES.includes(propertyType as PropertyType)) {
    errors.propertyType = "Choose a property type.";
  }
  if (!/^\d{6}$/.test(postalCode)) {
    errors.postalCode = "Enter a 6-digit postal code.";
  }
  if (!UNIT_PATTERN.test(unitNumber)) {
    errors.unitNumber = "Enter a unit such as #12-34.";
  }
  if (unitSize && (!/^\d+$/.test(unitSize) || Number(unitSize) <= 0)) {
    errors.unitSize = "Enter the size in whole square feet.";
  }
  if (renovationYear) {
    const currentYear = new Date().getFullYear();
    const earliestYear = currentYear - 24;
    const year = Number(renovationYear);
    if (!Number.isInteger(year) || year < earliestYear || year > currentYear) {
      errors.renovationYear = "Choose a year from the list.";
    }
  }
  if (
    renovationAmount &&
    (!/^\d+(?:\.\d{1,2})?$/.test(renovationAmount) || Number(renovationAmount) < 0)
  ) {
    errors.renovationAmount = "Enter a valid renovation amount.";
  }
  const isHdb = propertyType === "hdb";
  if (isHdb && hdbMopMet !== "yes" && hdbMopMet !== "no") {
    errors.hdbMopMet = "Tell us if the flat has met its MOP.";
  }
  if (isHdb && hdbMopMet === "no") {
    const years = Number(hdbYearsStayed);
    if (!/^\d{1,2}$/.test(hdbYearsStayed) || years > 10) {
      errors.hdbYearsStayed = "Enter the years you have lived there, from 0 to 10.";
    }
  }
  if (!name) {
    errors.name = "Enter your name.";
  }
  if (!EMAIL_PATTERN.test(email)) {
    errors.email = "Enter a valid email address.";
  }
  if (!MOBILE_PATTERN.test(mobile)) {
    errors.mobile = "Enter a Singapore mobile number that starts with 8 or 9.";
  }
  if (!consent) {
    errors.consent = "Please agree before requesting the report.";
  }

  if (Object.keys(errors).length > 0) {
    return { data: null, errors };
  }

  return {
    data: {
      propertyType: propertyType as PropertyType,
      postalCode,
      unitNumber,
      unitSize: unitSize || undefined,
      renovationYear: renovationYear || undefined,
      renovationAmount: renovationAmount || undefined,
      hdbMopMet: isHdb ? (hdbMopMet as "yes" | "no") : undefined,
      hdbYearsStayed: isHdb && hdbMopMet === "no" ? hdbYearsStayed : undefined,
      name,
      email,
      mobile,
      consent,
    },
    errors: {},
  };
}

export function propertyTypeLabel(type: string): string {
  if (type === "hdb") return "HDB";
  if (type === "ec") return "EC";
  if (type === "private-condo") return "Private condo";
  return "Property";
}
